import { useEffect, useState, useCallback } from "react";
import { X, Plus, Trash2, Route, Clock, Receipt, Pill, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Conceptos rápidos del catálogo oficial — botones de un toque
const QUICK_EXTRAS = [
    { code: "O2_BAS", label: "Oxígeno BLS", icon: "💨" },
    { code: "O2_AV", label: "Oxígeno avanzado", icon: "💨" },
    { code: "MONIT", label: "Monitoreo extra", icon: "📈" },
    { code: "BOMBA", label: "Bomba infusión", icon: "💊" },
    { code: "VENT", label: "Ventilador", icon: "🫁" },
    { code: "MED", label: "Médico a bordo", icon: "🩺" },
    { code: "PARA_X", label: "Paramédico extra", icon: "👤" },
    { code: "MEDIC", label: "Medicamento", icon: "💉" },
];

export default function ServiceChargesSheet({ service, onClose, onUpdated }) {
    const [data, setData] = useState(service);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("foreign_km");
    // Form states
    const [km, setKm] = useState("");
    const [waitMin, setWaitMin] = useState("");
    const [tollAmount, setTollAmount] = useState("");
    const [tollLabel, setTollLabel] = useState("");
    const [extraQty, setExtraQty] = useState({}); // {O2_BAS: 1, ...}
    const [extraMedAmount, setExtraMedAmount] = useState(""); // monto manual para MEDIC
    const [extraMedNotes, setExtraMedNotes] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data: fresh } = await api.get(`/services/${service.id}`);
            setData(fresh.service || fresh);
        } catch { /* ignore */ }
    }, [service.id]);

    useEffect(() => { setData(service); }, [service]);

    // Cargar sugerencias automáticas basadas en GPS al abrir
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data: res } = await api.get(`/services/${service.id}/suggest-charges`);
                if (alive) {
                    setSuggestions(res.suggestions || []);
                    setSuggestionsLoaded(true);
                }
            } catch {
                if (alive) setSuggestionsLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [service.id]);

    const applySuggestion = (sug) => {
        const payload = { type: sug.type, qty: sug.qty };
        addCharge(payload);
        setSuggestions((prev) => prev.filter((s) => s !== sug));
    };
    const dismissSuggestion = (sug) => setSuggestions((prev) => prev.filter((s) => s !== sug));

    const addCharge = async (payload) => {
        setBusy(true);
        try {
            const { data: res } = await api.post(`/services/${service.id}/add-charge`, payload);
            toast.success(`✅ ${res.extra_added.label} · ${money(res.extra_added.amount)} agregado. Total: ${money(res.total)}`);
            await refresh();
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al agregar cargo");
        } finally {
            setBusy(false);
        }
    };

    const submitKm = (e) => {
        e.preventDefault();
        const v = parseFloat(km);
        if (!v || v <= 0) return toast.error("Captura los km recorridos");
        addCharge({ type: "foreign_km", qty: v });
        setKm("");
    };
    const submitWait = (e) => {
        e.preventDefault();
        const v = parseFloat(waitMin);
        if (!v || v <= 0) return toast.error("Captura los minutos de espera");
        addCharge({ type: "wait_minutes", qty: v });
        setWaitMin("");
    };
    const submitToll = (e) => {
        e.preventDefault();
        const v = parseFloat(tollAmount);
        if (!v || v <= 0) return toast.error("Captura el monto de la caseta");
        addCharge({ type: "toll", amount: v, label: tollLabel || "Caseta / Peaje" });
        setTollAmount(""); setTollLabel("");
    };
    const submitExtra = (code, label, qty = 1, manual_amount = null, notes = "") => {
        const payload = { type: "extra", code, label, qty };
        if (manual_amount && manual_amount > 0) payload.unit_price = manual_amount;
        if (notes) payload.notes = notes;
        addCharge(payload);
    };

    const removeCharge = async (idx, extra) => {
        if (!window.confirm(`¿Eliminar "${extra.label}" (${money(extra.amount)})?`)) return;
        setBusy(true);
        try {
            await api.delete(`/services/${service.id}/charges/${idx}`);
            toast.success("Cargo eliminado");
            await refresh();
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Solo cabina/admin puede eliminar cargos");
        } finally {
            setBusy(false);
        }
    };

    const extras = data?.extras || [];
    const total = data?.total || 0;
    const subtotal = data?.subtotal || 0;
    const iva = data?.iva || 0;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4" data-testid="service-charges-sheet">
            <div className="bg-white w-full md:max-w-2xl md:rounded-sm shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cargos del servicio</div>
                        <div className="font-display font-bold text-base">{service.folio} <span className="text-slate-400 font-normal text-sm">· {service.client_name || service.patient_name || ""}</span></div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="close-charges-sheet"><X className="w-5 h-5" /></button>
                </div>

                {/* Totales en vivo */}
                <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between text-xs">
                    <div>
                        <div className="text-emerald-700 font-mono">{extras.length} cargo(s) extra · {money(extras.reduce((s, e) => s + (e.amount || 0), 0))}</div>
                        <div className="text-slate-500">Subtotal {money(subtotal)} · IVA {money(iva)}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[9px] uppercase tracking-widest text-slate-500">Total actual</div>
                        <div className="font-mono font-bold text-lg text-emerald-700" data-testid="charges-total">{money(total)}</div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-200">
                    {[
                        { id: "foreign_km", icon: Route, label: "Km foráneo" },
                        { id: "wait_minutes", icon: Clock, label: "Espera" },
                        { id: "extras", icon: Pill, label: "Insumos" },
                        { id: "toll", icon: Receipt, label: "Caseta" },
                    ].map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-2 px-2 text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 border-b-2 ${active ? "border-emerald-500 text-emerald-700 bg-emerald-50/50" : "border-transparent text-slate-500 hover:text-slate-900"}`} data-testid={`charges-tab-${t.id}`}>
                                <Icon className="w-3 h-3" /> {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex-1 overflow-y-auto fm-scroll p-4 space-y-3">
                    {/* === Sugerencias automáticas basadas en GPS === */}
                    {suggestions.length > 0 && (
                        <div className="border border-indigo-300 bg-indigo-50 rounded-sm p-3 space-y-2" data-testid="gps-suggestions">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-900 flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" /> Sugerencias automáticas (GPS)
                            </div>
                            {suggestions.map((sug, i) => (
                                <div key={i} className="flex items-center justify-between gap-2 bg-white border border-indigo-200 rounded-sm p-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-bold">
                                            {sug.type === "foreign_km" && `🛣️ ${sug.qty.toFixed(1)} km foráneo`}
                                            {sug.type === "wait_minutes" && `⏱️ ${sug.qty} min de espera`}
                                            {" · "}
                                            <span className="font-mono text-emerald-700">~{money(sug.estimated_amount)}</span>
                                        </div>
                                        <div className="text-[10px] text-slate-600 mt-0.5">{sug.reason}</div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button onClick={() => applySuggestion(sug)} disabled={busy} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm" data-testid={`apply-suggestion-${sug.type}`}>Agregar</button>
                                        <button onClick={() => dismissSuggestion(sug)} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-slate-300 hover:bg-slate-100 text-slate-600 rounded-sm">Omitir</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {suggestionsLoaded && suggestions.length === 0 && (
                        <div className="text-[10px] text-slate-400 italic text-center py-1">
                            ✓ GPS analizado · sin sugerencias automáticas (rutas urbanas y sin esperas prolongadas).
                        </div>
                    )}
                    {/* Tab Km foráneo */}
                    {tab === "foreign_km" && (
                        <form onSubmit={submitKm} className="space-y-3">
                            <div className="text-xs text-slate-600">Captura los <strong>km recorridos fuera del área metropolitana</strong>. Si el servicio es ida y vuelta y el tarifario lo indica, el sistema los duplica automáticamente.</div>
                            <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                    <label className="fm-label">Kilómetros</label>
                                    <input type="number" step="1" min="1" value={km} onChange={(e) => setKm(e.target.value)} className="fm-input text-lg font-mono" placeholder="Ej. 45" data-testid="charge-km-input" />
                                    <div className="text-[10px] text-slate-500 mt-1 font-mono">~ Aprox: {money((parseFloat(km) || 0) * 90)} {service.is_round_trip ? "(verificar regla ida y vuelta)" : ""}</div>
                                </div>
                                <button type="submit" disabled={busy} className="fm-btn-accent" data-testid="charge-km-submit"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                            </div>
                        </form>
                    )}

                    {/* Tab Espera */}
                    {tab === "wait_minutes" && (
                        <form onSubmit={submitWait} className="space-y-3">
                            <div className="text-xs text-slate-600">Tiempo total que la unidad esperó (trámite de alta, autorización, camillero...). El sistema cobra <strong>bloques de 30 min × $450</strong> respetando la gracia configurada.</div>
                            <div className="grid grid-cols-3 gap-2">
                                {[30, 60, 90, 120, 180, 240].map((m) => (
                                    <button key={m} type="button" onClick={() => setWaitMin(String(m))} className={`fm-btn-outline text-xs ${waitMin === String(m) ? "border-emerald-500 bg-emerald-50" : ""}`} data-testid={`wait-quick-${m}`}>{m} min</button>
                                ))}
                            </div>
                            <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                    <label className="fm-label">Minutos exactos</label>
                                    <input type="number" min="1" value={waitMin} onChange={(e) => setWaitMin(e.target.value)} className="fm-input text-lg font-mono" placeholder="Ej. 45" data-testid="charge-wait-input" />
                                    <div className="text-[10px] text-slate-500 mt-1 font-mono">~ Aprox: {money(Math.ceil((parseFloat(waitMin) || 0) / 30) * 450)}</div>
                                </div>
                                <button type="submit" disabled={busy} className="fm-btn-accent" data-testid="charge-wait-submit"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                            </div>
                        </form>
                    )}

                    {/* Tab Insumos / Extras clínicos */}
                    {tab === "extras" && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-600">Marca cada insumo o servicio clínico extra que se utilizó. El precio se toma del tarifario aplicado.</div>
                            <div className="grid grid-cols-2 gap-2">
                                {QUICK_EXTRAS.filter((e) => e.code !== "MEDIC").map((qe) => (
                                    <button
                                        key={qe.code}
                                        type="button"
                                        disabled={busy}
                                        onClick={() => submitExtra(qe.code, qe.label, extraQty[qe.code] || 1)}
                                        className="border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-sm p-2 text-left text-xs flex items-start gap-2 transition-colors"
                                        data-testid={`extra-${qe.code}`}
                                    >
                                        <span className="text-lg">{qe.icon}</span>
                                        <div className="flex-1">
                                            <div className="font-bold">{qe.label}</div>
                                            <div className="text-[10px] text-slate-500">Toca para agregar</div>
                                        </div>
                                        {(qe.code === "BOMBA" || qe.code === "PARA_X") && (
                                            <input
                                                type="number"
                                                min="1"
                                                defaultValue={1}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => setExtraQty({ ...extraQty, [qe.code]: parseInt(e.target.value, 10) || 1 })}
                                                className="fm-input w-12 text-center text-xs px-1 py-0.5"
                                                title="Cantidad"
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Medicamento variable */}
                            <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 space-y-2">
                                <div className="text-xs font-bold flex items-center gap-1">💉 Medicamento / monto manual</div>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        min="1"
                                        step="50"
                                        value={extraMedAmount}
                                        onChange={(e) => setExtraMedAmount(e.target.value)}
                                        className="fm-input flex-1 text-sm font-mono"
                                        placeholder="Costo MXN"
                                        data-testid="extra-medic-amount"
                                    />
                                    <input
                                        type="text"
                                        value={extraMedNotes}
                                        onChange={(e) => setExtraMedNotes(e.target.value)}
                                        className="fm-input flex-1 text-sm"
                                        placeholder="Ej. Adrenalina 1 amp."
                                    />
                                </div>
                                <button
                                    type="button"
                                    disabled={busy || !extraMedAmount}
                                    onClick={() => {
                                        const v = parseFloat(extraMedAmount);
                                        if (!v) return;
                                        submitExtra("MEDIC", extraMedNotes || "Medicamento", 1, v, extraMedNotes);
                                        setExtraMedAmount(""); setExtraMedNotes("");
                                    }}
                                    className="fm-btn-accent w-full justify-center"
                                    data-testid="extra-medic-submit"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Agregar medicamento
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tab Caseta */}
                    {tab === "toll" && (
                        <form onSubmit={submitToll} className="space-y-3">
                            <div className="text-xs text-slate-600">Captura cada caseta/peaje pagado en la ruta. Se reembolsa al cobrar el servicio.</div>
                            <div>
                                <label className="fm-label">Monto</label>
                                <input type="number" step="1" min="1" value={tollAmount} onChange={(e) => setTollAmount(e.target.value)} className="fm-input text-lg font-mono" placeholder="Ej. 250" data-testid="charge-toll-input" />
                            </div>
                            <div>
                                <label className="fm-label">Descripción (opcional)</label>
                                <input type="text" value={tollLabel} onChange={(e) => setTollLabel(e.target.value)} className="fm-input" placeholder="Ej. Caseta Tepotzotlán" />
                            </div>
                            <button type="submit" disabled={busy} className="fm-btn-accent w-full justify-center" data-testid="charge-toll-submit"><Plus className="w-3.5 h-3.5" /> Agregar caseta</button>
                        </form>
                    )}

                    {/* Lista de cargos ya agregados */}
                    {extras.length > 0 && (
                        <div className="pt-4 border-t border-slate-200">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Cargos agregados ({extras.length})</div>
                            <div className="space-y-1.5">
                                {extras.map((e, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-50 border-l-2 border-emerald-400 rounded-sm text-xs">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold truncate">{e.label}</div>
                                            <div className="text-[10px] text-slate-500 truncate">
                                                {e.added_by && <span>{e.added_by} · </span>}
                                                {e.added_at && <span>{new Date(e.added_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>}
                                                {e.notes && <span> · {e.notes}</span>}
                                            </div>
                                        </div>
                                        <div className="font-mono font-bold text-emerald-700">{money(e.amount)}</div>
                                        <button onClick={() => removeCharge(i, e)} className="text-red-500 hover:text-red-700" title="Eliminar (solo cabina/admin)">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t border-slate-200 px-4 py-2 bg-slate-50 flex items-center justify-between">
                    <div className="text-[10px] text-slate-500">Los cargos se reflejan al instante en el cobro del servicio.</div>
                    <button onClick={onClose} className="fm-btn-primary text-xs" data-testid="close-charges-done">Listo</button>
                </div>
            </div>
        </div>
    );
}
